import DateTimeField from "../../components/DateTimeField";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { nullableMetricNumber, metricPercentage, formatMetricValue, averageSaleValue } from '../../utils/commercialMetricPresentation';
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  OutlinedInput,
  TextField,
  Typography,
  Paper,
  Box,
  IconButton,
  Popover,
  useTheme,
  useMediaQuery,
  Collapse,
  Button,
  Chip,
} from "@material-ui/core";

// Ícones
import ChatRoundedIcon from "@material-ui/icons/ChatRounded";
import PeopleAltRoundedIcon from "@material-ui/icons/PeopleAltRounded";
import HourglassEmptyRoundedIcon from "@material-ui/icons/HourglassEmptyRounded";
import CheckCircleRoundedIcon from "@material-ui/icons/CheckCircleRounded";
import AccessTimeRoundedIcon from "@material-ui/icons/AccessTimeRounded";
import TimerRoundedIcon from "@material-ui/icons/TimerRounded";
import FilterListRoundedIcon from "@material-ui/icons/FilterListRounded";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import TrendingDownIcon from "@material-ui/icons/TrendingDown";
import MonetizationOnOutlinedIcon from "@material-ui/icons/MonetizationOnOutlined";
import CalendarTodayIcon from "@material-ui/icons/CalendarToday";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ExpandLessIcon from "@material-ui/icons/ExpandLess";
import FullscreenIcon from "@material-ui/icons/Fullscreen";
import FullscreenExitIcon from "@material-ui/icons/FullscreenExit";

// Gráficos
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { toast } from "react-toastify";
import { isEmpty, isArray } from "lodash";
import moment from "moment";

import ButtonWithSpinner from "../../components/ButtonWithSpinner";
import PageContentLoader from "../../components/PageContentLoader";
import PageRefreshIndicator from "../../components/PageRefreshIndicator";
import useContacts from "../../hooks/useContacts";
import { usePageHeaderMode } from "../../hooks/useTabletPortrait";
import { i18n } from "../../translate/i18n";
import { useDashboardSummaryQuery } from "../../queries/dashboard";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    minHeight: '100%',
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      overflowX: "hidden",
      maxWidth: "100vw",
      width: "100%",
    },
  },
  container: {
    maxWidth: 1600,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    // Same side edges as the other pages.
    "html[data-tablet-header] &": { paddingLeft: 28, paddingRight: 28 },
    'html[data-page-header="phone"] &': { paddingLeft: 16, paddingRight: 16 },
    [theme.breakpoints.up("sm")]: {
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
    },
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      maxWidth: "100%",
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      boxSizing: "border-box",
      overflowX: "hidden",
    },
  },
  pageHeader: {
    marginBottom: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    // Same header row as the other pages on a portrait tablet.
    // On a phone: title and filters on the first line, the board and period on the second.
    'html[data-page-header="phone"][data-tablet-header] &': { rowGap: 8, marginTop: 0, marginBottom: 0, alignItems: "center", flexWrap: "wrap" },
    "html[data-tablet-header] &": { minHeight: 33, marginTop: theme.spacing(2), marginBottom: theme.spacing(1) },
  },
  pageTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: theme.palette.text.primary,
    letterSpacing: '-0.5px',
    "html[data-tablet-header] &": { ...theme.typography.h5, color: theme.palette.primary.main, letterSpacing: "normal" },
  },
  dateRange: {
    "html[data-tablet-header] &": { height: 33, boxSizing: "border-box" },
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 12,
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      overflow: "hidden",
      whiteSpace: "nowrap",
    },
  },
  metricsRow: {
    marginBottom: theme.spacing(3),
  },
  hideOnThreeColumns: {
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  threeColumnsOnly: {
    display: "none",
    [theme.breakpoints.up("md")]: { display: "block" },
  },
  twoColumnsOnly: {
    display: "none",
    [theme.breakpoints.up("sm")]: { display: "block" },
    [theme.breakpoints.up("md")]: { display: "none" },
  },
  metricCard: {
    backgroundColor: 'var(--dashboard-metric-bg)',
    borderRadius: 12,
    padding: theme.spacing(2),
    height: '100%',
    position: 'relative',
    overflow: 'hidden',
    border: theme.neoUI.effects.contour.main,
    boxShadow: theme.neoUI.floating.button.boxShadow, // Specific request: Green cards use button shadow
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    cursor: 'default',
    '&:hover': {
      transform: theme.neoUI.floating.button.floating.transform,
      boxShadow: theme.neoUI.floating.button.floating.boxShadow,
    },
  },
  metricContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '100%',
  },
  metricInfo: {
    flex: 1,
    minWidth: 0,
  },
  metricTitleRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.6),
  },
  metricLabel: {
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--dashboard-metric-text)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    minWidth: 0,
  },
  metricValue: {
    fontSize: 26,
    fontWeight: 700,
    color: 'var(--dashboard-metric-text)',
    lineHeight: 1.2,
  },
  metricChange: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.4),
    fontSize: 10,
    fontWeight: 700,
    lineHeight: 1,
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  positiveChange: {
    color: theme.palette.type === 'dark'
      ? theme.palette.primary.main
      : theme.palette.success.main,
  },
  negativeChange: {
    color: theme.palette.type === 'dark'
      ? theme.palette.error.main
      : theme.palette.error.main,
  },
  neutralChange: {
    color: theme.palette.text.secondary,
  },
  metricIconBox: {
    width: 45,
    height: 45,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(1.6),
    backgroundColor: 'var(--dashboard-metric-icon-bg)',
    '& svg': {
      fontSize: 19,
      color: 'var(--dashboard-metric-icon-color)',
    },
  },
  chartCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    height: '100%',
    border: theme.neoUI.effects.contour.main,
    boxShadow: theme.neoUI.floating.container.boxShadow,
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '&:hover': {
      transform: theme.neoUI.floating.container.transform,
      boxShadow: theme.neoUI.floating.container.hoverShadow,
    },
  },
  pieChartCard: { // Same behavior as chartCard now, kept for semantic differentiation if needed or we can merge
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    height: '100%',
    border: theme.neoUI.effects.contour.main,
    boxShadow: theme.neoUI.floating.container.boxShadow,
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '&:hover': {
      transform: theme.neoUI.floating.container.transform,
      boxShadow: theme.neoUI.floating.container.hoverShadow,
    },
  },
  // SVG Filters for Recharts
  barElement: {
    filter: theme.neoUI.floating.button.boxShadow.split(',').map(s => `drop-shadow(${s.trim()})`).join(' '),
    transition: 'filter 0.3s ease',
    '&:hover': {
      filter: theme.neoUI.floating.button.hoverShadow.split(',').map(s => `drop-shadow(${s.trim()})`).join(' '),
    }
  },
  pieElement: {
    filter: theme.neoUI.floating.button.boxShadow.split(',').map(s => `drop-shadow(${s.trim()})`).join(' '),
  },
  chartHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  chartTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  chartSubtitle: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    marginTop: 2,
  },
  chartHeaderControls: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  chartSelect: {
    minWidth: 190,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  boardChip: {
    "html[data-tablet-header] &": { height: 33, boxSizing: "border-box" },
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 1.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    color: theme.palette.text.secondary,
    fontSize: 12,
    maxWidth: 220,
    "& > span": {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
  },
  filterSection: {
    marginBottom: theme.spacing(2),
    'html[data-page-header="phone"] &': { marginBottom: 12 },
  },
  headerChips: {
    'html[data-page-header="phone"] &': {
      flex: "1 0 100%",
      minWidth: 0,
      "& > $boardChip, & > $dateRange": { flex: "1 1 0", minWidth: 0, justifyContent: "flex-start" },
      "& .MuiTypography-root": { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
    },
  },
  // Botão de filtro padronizado usando token do tema NeoUI (variante outline)
  filterButton: {
    ...theme.neoUI?.buttons?.action?.root,
    // Preserva dimensões originais do Dashboard
    height: 40,
    minHeight: 40,
    "html[data-tablet-header] &": { height: 33, minHeight: 33 },
    borderRadius: 10,
    padding: theme.spacing(0, 2),
    // Estilo outline/secundário para toggle
    backgroundColor: `${theme.palette.primary.main}15`,
    color: theme.palette.type === 'dark' ? '#ffffff' : theme.palette.primary.main,
    border: `1px solid ${theme.palette.primary.main}30`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}25`,
      boxShadow: theme.neoUI?.floating?.button?.hoverShadow,
    },
  },
  filterContent: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    border: theme.neoUI.effects.contour.main,
    boxShadow: theme.neoUI.effects.glow.main,
  },
  filterGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: theme.spacing(2),
    alignItems: "center",
    [theme.breakpoints.down("md")]: {
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    },
    [theme.breakpoints.down("xs")]: {
      gridTemplateColumns: "1fr",
    },
  },
  filterField: {
    minWidth: 0,
  },
  // Botão de ação padronizado usando token do tema NeoUI
  applyButton: {
    ...theme.neoUI?.buttons?.action?.root,
    // Preserva dimensões originais do Dashboard
    height: 40,
    minHeight: 40,
    borderRadius: 10,
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 300,
  },
  statItem: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  statLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  statValue: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  tableCard: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: 16,
    padding: theme.spacing(2.5),
    border: theme.neoUI.effects.contour.main,
    boxShadow: theme.neoUI.floating.container.boxShadow,
    transition: 'all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1)',
    '&:hover': {
      transform: theme.neoUI.floating.container.transform,
      boxShadow: theme.neoUI.floating.container.hoverShadow,
    },
  },
  fixedDashboardCard: {
    height: 460,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      minHeight: 460,
    },
  },
  expandedDashboardCard: {
    height: "auto",
    minHeight: 460,
    display: "flex",
    flexDirection: "column",
  },
  dashboardGridRow: {
    "& > .MuiGrid-item": {
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
    [theme.breakpoints.down("md")]: {
      "& > .MuiGrid-item + .MuiGrid-item": {
        marginTop: theme.spacing(2),
      },
    },
    "&.MuiGrid-spacing-xs-2": {
      marginTop: 0,
      marginBottom: 0,
    },
  },
  finalDashboardRow: {
    marginTop: "16px !important",
  },
  lossReasonBody: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
  },
  lossReasonLegend: {
    flex: "0 0 auto",
    minWidth: 0,
    width: "100%",
  },
  lossReasonChartArea: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 150,
  },
  lossReasonRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.75),
  },
  lossReasonName: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  lossReasonLabel: {
    fontSize: 12,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lossReasonValue: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
  lossReasonOthers: {
    cursor: "pointer",
    borderRadius: 6,
    padding: theme.spacing(0.25, 0),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  lossReasonPopover: {
    padding: theme.spacing(1.5),
    maxWidth: 280,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: theme.neoUI.floating.container.boxShadow,
  },
  attendantsCardHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  attendantsTableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowX: "auto",
    overflowY: "hidden",
  },
  lossItemsCardHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    [theme.breakpoints.down("xs")]: {
      flexDirection: "column",
    },
  },
  lossItemsHeaderActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down("xs")]: {
      width: "100%",
    },
  },
  lossItemsFilter: {
    minWidth: 170,
    [theme.breakpoints.down("xs")]: {
      flex: 1,
      minWidth: 0,
    },
  },
  lossItemsTableWrapper: {
    flex: 1,
    minHeight: 0,
    overflowX: "auto",
  },
  lossItemsRow: {
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  lossItemsTitle: {
    fontSize: 12,
    fontWeight: 700,
    maxWidth: 230,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lossItemsMeta: {
    fontSize: 10,
    color: theme.palette.text.secondary,
    maxWidth: 230,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  lossItemsEmpty: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  expandButton: {
    width: 34,
    height: 34,
    color: theme.palette.text.secondary,
    "& svg": {
      fontSize: "1.5rem",
    },
  },
  compactTable: {
    '& th': {
      fontSize: 11,
      padding: theme.spacing(1.5),
    },
    '& td': {
      fontSize: 12,
      padding: theme.spacing(1.5),
    },
  },
  dateField: {
    '& .MuiInputAdornment-root svg': {
      color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.secondary,
    },
    '& .MuiOutlinedInput-root': {
      '& .MuiInputAdornment-root svg': {
        color: theme.palette.mode === 'dark' ? '#ffffff' : theme.palette.text.secondary,
      },
    },
  },
}));

const PERFORMANCE_INDICATORS = [
  { value: "none", labelKey: "dashboard.performanceIndicators.none", fallback: "Nenhum", type: "none" },
  { value: "tickets", labelKey: "dashboard.performanceIndicators.tickets", fallback: "Atendimentos", type: "integer" },
  { value: "wonCount", labelKey: "dashboard.performanceIndicators.wonCount", fallback: "Número de Vendas", type: "integer" },
  { value: "lostCount", labelKey: "dashboard.performanceIndicators.lostCount", fallback: "Número de Perdas", type: "integer" },
  { value: "wonValue", labelKey: "dashboard.performanceIndicators.wonValue", fallback: "Valor Faturado", type: "currency" },
  { value: "lostValue", labelKey: "dashboard.performanceIndicators.lostValue", fallback: "Valor Perdido", type: "currency" },
  { value: "conversionRate", labelKey: "dashboard.performanceIndicators.conversionRate", fallback: "Taxa de Conversão", type: "percent" },
  { value: "avgSupportTime", labelKey: "dashboard.performanceIndicators.avgSupportTime", fallback: "Tempo médio de atendimento", type: "time" },
  { value: "avgConversionTime", labelKey: "dashboard.performanceIndicators.avgConversionTime", fallback: "Tempo médio de conversão", type: "time" },
];

const DASHBOARD_MEMORY_PREFIX = "dashboard:filters";

const readDashboardMemory = (companyId) => {
  if (!companyId || typeof window === "undefined") return {};
  try {
    return JSON.parse(localStorage.getItem(`${DASHBOARD_MEMORY_PREFIX}:${companyId}`) || "{}");
  } catch (err) {
    return {};
  }
};

const normalizeDateValue = (value, fallback) =>
  value && moment(value).isValid() ? moment(value).format("YYYY-MM-DD") : fallback;

const LOSS_REASON_VISIBLE_LIMIT = 5;
const LOSS_ITEMS_COLLAPSED_LIMIT = 5;
const LOSS_ITEMS_EXPANDED_FALLBACK_LIMIT = 500;

const Dashboard = () => {
  const classes = useStyles();
  const isPhone = usePageHeaderMode() === "phone";
  const history = useHistory();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const companyId = localStorage.getItem("companyId");
  const initialMemory = useMemo(() => readDashboardMemory(companyId), [companyId]);
  const defaultDateFrom = moment("1", "D").format("YYYY-MM-DD");
  const defaultDateTo = moment().format("YYYY-MM-DD");
  const initialFilterType = Number(initialMemory.filterType) === 2 ? 2 : 1;
  const initialPeriod = Number(initialMemory.period || 0);
  const initialDateFrom = normalizeDateValue(initialMemory.dateFrom, defaultDateFrom);
  const initialDateTo = normalizeDateValue(initialMemory.dateTo, defaultDateTo);
  const initialKanbanBoardId = initialMemory.selectedKanbanBoardId || "";

  // Estados originais do seu código
  const [period, setPeriod] = useState(initialFilterType === 2 ? initialPeriod : 0);
  const [filterType, setFilterType] = useState(initialFilterType);
  const [dateFrom, setDateFrom] = useState(initialFilterType === 1 ? initialDateFrom : "");
  const [dateTo, setDateTo] = useState(initialFilterType === 1 ? initialDateTo : "");
  const [showFilters, setShowFilters] = useState(Boolean(initialMemory.showFilters));
  const [kanbanBoards, setKanbanBoards] = useState([]);
  const [selectedKanbanBoardId, setSelectedKanbanBoardId] = useState(initialKanbanBoardId);
  const [lossReasonUserId, setLossReasonUserId] = useState("");
  const [lossReasonOthersAnchorEl, setLossReasonOthersAnchorEl] = useState(null);
  const [lossReasonItemFilter, setLossReasonItemFilter] = useState("");
  const [lossReasonItemsExpanded, setLossReasonItemsExpanded] = useState(false);
  const [lossReasonItems, setLossReasonItems] = useState([]);
  const [lossReasonItemsCount, setLossReasonItemsCount] = useState(0);
  const [lossReasonItemsLoading, setLossReasonItemsLoading] = useState(false);
  const [performanceIndicator1, setPerformanceIndicator1] = useState(
    initialMemory.performanceIndicator1 || "tickets"
  );
  const [performanceIndicator2, setPerformanceIndicator2] = useState(
    initialMemory.performanceIndicator2 || "none"
  );
  const [appliedParams, setAppliedParams] = useState(() => {
    const params = {};
    if (initialFilterType === 2 && initialPeriod > 0) {
      params.days = initialPeriod;
    }
    if (initialFilterType === 1) {
      params.date_from = initialDateFrom;
      params.date_to = initialDateTo;
    }
    if (initialKanbanBoardId) {
      params.kanbanBoardId = initialKanbanBoardId;
    }
    return params;
  });
  const {
    data: dashboardData,
    isLoading,
    isFetching,
  } = useDashboardSummaryQuery({
    companyId,
    params: appliedParams,
    enabled: Boolean(companyId),
  });
  const counters = dashboardData?.counters || {};
  const attendants = isArray(dashboardData?.attendants) ? dashboardData.attendants : [];
  const lossReasons = isArray(dashboardData?.lossReasons) ? dashboardData.lossReasons : [];
  const { count: contactsCount = 0 } = useContacts({});

  useEffect(() => {
    let active = true;
    api.get("/kanban/boards")
      .then(({ data }) => {
        if (!active) return;
        const boards = Array.isArray(data?.boards) ? data.boards : [];
        setKanbanBoards(boards);
      })
      .catch(() => {
        if (active) setKanbanBoards([]);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!selectedKanbanBoardId || kanbanBoards.length === 0) return;
    const exists = kanbanBoards.some(board => String(board.id) === String(selectedKanbanBoardId));
    if (!exists) {
      setSelectedKanbanBoardId("");
      setAppliedParams(prev => {
        const { kanbanBoardId, ...nextParams } = prev;
        return nextParams;
      });
    }
  }, [kanbanBoards, selectedKanbanBoardId]);

  useEffect(() => {
    if (!companyId || typeof window === "undefined") return;
    localStorage.setItem(
      `${DASHBOARD_MEMORY_PREFIX}:${companyId}`,
      JSON.stringify({
        filterType,
        period,
        dateFrom,
        dateTo,
        selectedKanbanBoardId,
        performanceIndicator1,
        performanceIndicator2,
        showFilters
      })
    );
  }, [
    companyId,
    filterType,
    period,
    dateFrom,
    dateTo,
    selectedKanbanBoardId,
    performanceIndicator1,
    performanceIndicator2,
    showFilters
  ]);

  const selectedKanbanBoard = useMemo(
    () => kanbanBoards.find(board => String(board.id) === String(selectedKanbanBoardId)) || null,
    [kanbanBoards, selectedKanbanBoardId]
  );

  const getKanbanBoardFilterLabel = (value) => {
    if (!value) {
      const translated = i18n.t("dashboard.filters.allKanbanBoards");
      return translated === "dashboard.filters.allKanbanBoards"
        ? "Todos os quadros"
        : translated;
    }

    const board = kanbanBoards.find(item => String(item.id) === String(value));
    return board?.name || value;
  };

  async function handleChangePeriod(value) {
    setPeriod(value);
  }

  async function handleChangeFilterType(value) {
    setFilterType(value);
    if (value === 1) {
      setPeriod(0);
    } else {
      setDateFrom("");
      setDateTo("");
    }
  }

  function buildDashboardParams() {
    let params = {};

    if (period > 0) {
      params = {
        days: period,
      };
    }

    if (!isEmpty(dateFrom) && moment(dateFrom).isValid()) {
      params = {
        ...params,
        date_from: moment(dateFrom).format("YYYY-MM-DD"),
      };
    }

    if (!isEmpty(dateTo) && moment(dateTo).isValid()) {
      params = {
        ...params,
        date_to: moment(dateTo).format("YYYY-MM-DD"),
      };
    }

    if (selectedKanbanBoardId) {
      params = {
        ...params,
        kanbanBoardId: selectedKanbanBoardId,
      };
    }

    if (lossReasonUserId) {
      params = {
        ...params,
        lossReasonUserId,
      };
    }

    if (Object.keys(params).length === 0) {
      return null;
    }

    return params;
  }

  function fetchData() {
    const params = buildDashboardParams();
    if (!params) {
      toast.error(i18n.t("dashboard.toasts.selectFilterError"));
      return;
    }

    setAppliedParams(params);
  }

  const handleLossReasonUserChange = (event) => {
    const value = event.target.value;
    setLossReasonUserId(value);
    setAppliedParams((previous) => {
      const next = { ...(previous || {}) };
      if (value) {
        next.lossReasonUserId = value;
      } else {
        delete next.lossReasonUserId;
      }
      return next;
    });
  };

  function formatTime(minutes) {
    return moment()
      .startOf("day")
      .add(minutes, "minutes")
      .format("HH[h] mm[m]");
  }

  // Dados para os gráficos baseados nos counters reais
  const totalTickets = (counters.supportFinished || 0) + (counters.supportHappening || 0) + (counters.supportPending || 0);

  // Cores primárias do tema para o gráfico de pizza
  const pieColors = [
    theme.palette.primary.main,
    `${theme.palette.primary.main}80`,
    `${theme.palette.primary.main}40`,
  ];

  const pieChartData = [
    {
      name: i18n.t("dashboard.status.finished") || 'Finalizados',
      value: counters.supportFinished || 0,
      percentage: counters.supportFinished ?
        ((counters.supportFinished / totalTickets) * 100).toFixed(1) : 0,
      color: pieColors[0]
    },
    {
      name: i18n.t("dashboard.status.inProgress") || 'Em Andamento',
      value: counters.supportHappening || 0,
      percentage: counters.supportHappening ?
        ((counters.supportHappening / totalTickets) * 100).toFixed(1) : 0,
      color: pieColors[1]
    },
    {
      name: i18n.t("dashboard.status.pending") || 'Pendentes',
      value: counters.supportPending || 0,
      percentage: counters.supportPending ?
        ((counters.supportPending / totalTickets) * 100).toFixed(1) : 0,
      color: pieColors[2]
    },
  ];

  const lossReasonTotal = lossReasons.reduce(
    (sum, item) => sum + Number(item.count || 0),
    0
  );

  const lossReasonBaseData = lossReasons.map((item) => ({
    ...item,
    value: Number(item.count || 0),
    percentage: Number(item.percentage || 0),
  }));

  const semMotivoItem = lossReasonBaseData.find(
    item => item.lossReasonId === null || item.id === "none" || item.name === "Sem motivo"
  );
  let visibleLossReasonItems = lossReasonBaseData.slice(0, LOSS_REASON_VISIBLE_LIMIT);

  if (
    semMotivoItem &&
    !visibleLossReasonItems.some(item => item.id === semMotivoItem.id)
  ) {
    visibleLossReasonItems = [
      ...visibleLossReasonItems.slice(0, LOSS_REASON_VISIBLE_LIMIT - 1),
      semMotivoItem
    ];
  }

  const visibleLossReasonKeys = new Set(
    visibleLossReasonItems.map(item => `${item.id}-${item.lossReasonId}`)
  );
  const lossReasonOthers = lossReasonBaseData.filter(
    item => !visibleLossReasonKeys.has(`${item.id}-${item.lossReasonId}`)
  );
  const lossReasonOthersCount = lossReasonOthers.reduce(
    (sum, item) => sum + Number(item.value || 0),
    0
  );
  const lossReasonDisplayData = [
    ...visibleLossReasonItems,
    ...(lossReasonOthersCount > 0
      ? [{
        id: "others",
        lossReasonId: "others",
        name: "Outros",
        value: lossReasonOthersCount,
        count: lossReasonOthersCount,
        percentage:
          lossReasonTotal > 0 ? Number((lossReasonOthersCount / lossReasonTotal * 100).toFixed(1)) : 0,
        isOthers: true
      }]
      : [])
  ].map((item, index) => ({
    ...item,
    color: pieColors[index % pieColors.length]
  }));
  const lossReasonVisibleRows = Math.max(lossReasonDisplayData.length, 1);
  const lossReasonChartHeight = Math.max(
    isSmallScreen ? 170 : 160,
    Math.min(isSmallScreen ? 230 : 260, 300 - lossReasonVisibleRows * 20)
  );
  const lossReasonOuterRadius = Math.max(
    62,
    Math.min(104, Math.floor(lossReasonChartHeight * 0.39))
  );
  const lossReasonInnerRadius = Math.floor(lossReasonOuterRadius * 0.62);

  const lossReasonItemOptions = lossReasonBaseData.map((item) => ({
    value: item.lossReasonId === null || item.id === "none" ? "none" : String(item.lossReasonId || item.id),
    label: item.name,
  }));
  const lossReasonLabelByValue = new Map(
    lossReasonItemOptions.map((item) => [String(item.value), item.label])
  );
  const selectedLossReasonItemOption = lossReasonItemOptions.find(
    item => String(item.value) === String(lossReasonItemFilter)
  );

  const formatCurrency = (value) => {
    const numericValue = Number(value || 0);
    if (!numericValue) return "-";
    return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(numericValue);
  };

  const formatLossDate = (value) => {
    if (!value || !moment(value).isValid()) return "-";
    return moment(value).format("DD/MM/YYYY HH:mm");
  };

  const performanceData = useMemo(
    () =>
      attendants
        .map((att) => ({
          name: att.name?.split(" ")[0] || "Atendente",
          tickets: Number(att.tickets || 0),
          wonCount: Number(att.wonCount || 0),
          lostCount: Number(att.lostCount || 0),
          wonValue: Number(att.wonValue || 0),
          lostValue: Number(att.lostValue || 0),
          conversionRate: metricPercentage(att.conversionRate),
          avgSupportTime: Number(att.avgSupportTime || 0),
          avgConversionTime: nullableMetricNumber(att.avgConversionTime),
        }))
        .slice(0, 7),
    [attendants]
  );

  const getIndicatorConfig = (value) =>
    PERFORMANCE_INDICATORS.find(indicator => indicator.value === value) || PERFORMANCE_INDICATORS[0];

  const LOSS_INDICATORS = new Set(["lostCount", "lostValue"]);
  const isLossIndicator = (value) => LOSS_INDICATORS.has(value);

  const translateIndicator = (indicator) => {
    const translated = i18n.t(indicator.labelKey);
    return translated === indicator.labelKey ? indicator.fallback : translated;
  };

  const computeAxisDomain = (values, type) => {
    const numbers = values.map((v) => Number(v) || 0);
    const max = Math.max(0, ...numbers);
    if (max === 0) return [0, type === "percent" ? 5 : 1];
    if (type === "integer") {
      return [0, Math.ceil(max * 1.25)];
    }
    return [0, max * 1.25];
  };

  const formatIndicatorValue = (value, type) => {
    return formatMetricValue(value, type, formatTime);
  };

  const performanceIndicatorConfig1 = getIndicatorConfig(performanceIndicator1);
  const performanceIndicatorConfig2 = getIndicatorConfig(performanceIndicator2);
  const hasPerformanceIndicator1 = performanceIndicatorConfig1.value !== "none";
  const hasPerformanceIndicator2 = performanceIndicatorConfig2.value !== "none";
  const performanceChartData = useMemo(
    () =>
      performanceData.map(item => ({
        ...item,
        [`${performanceIndicatorConfig1.value}Type`]: performanceIndicatorConfig1.type,
        [`${performanceIndicatorConfig2.value}Type`]: performanceIndicatorConfig2.type,
      })),
    [performanceData, performanceIndicatorConfig1, performanceIndicatorConfig2]
  );

  // Cálculo de mudanças reais baseado em dados anteriores
  const calculateChange = (current, previous) => {
    if (previous === null || previous === undefined) {
      return null;
    }
    if (Number(previous) === 0) {
      return Number(current || 0) === 0 ? 0 : null;
    }
    return Number(((current - previous) / previous * 100).toFixed(1));
  };

  const outcomeTrackingEnabled = counters.outcomeTrackingEnabled !== false;
  const lossReasonItemsParams = useMemo(() => {
    const params = {
      ...(appliedParams || {}),
      pageNumber: 1,
      limit: lossReasonItemsExpanded
        ? Math.max(lossReasonItemsCount || 0, LOSS_ITEMS_EXPANDED_FALLBACK_LIMIT)
        : LOSS_ITEMS_COLLAPSED_LIMIT,
    };

    if (lossReasonItemFilter) {
      params.lossReasonId = lossReasonItemFilter;
    }

    return params;
  }, [appliedParams, lossReasonItemFilter, lossReasonItemsCount, lossReasonItemsExpanded]);

  useEffect(() => {
    let active = true;

    if (!dashboardData || !outcomeTrackingEnabled) {
      setLossReasonItems([]);
      setLossReasonItemsCount(0);
      setLossReasonItemsLoading(false);
      return undefined;
    }

    setLossReasonItemsLoading(true);
    api.get("/dashboard/loss-reason-items", { params: lossReasonItemsParams })
      .then(({ data }) => {
        if (!active) return;
        setLossReasonItems(Array.isArray(data?.items) ? data.items : []);
        setLossReasonItemsCount(Number(data?.count || 0));
      })
      .catch((err) => {
        if (!active) return;
        setLossReasonItems([]);
        setLossReasonItemsCount(0);
        toast.error(err?.response?.data?.message || "Erro ao carregar tickets perdidos");
      })
      .finally(() => {
        if (active) setLossReasonItemsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [dashboardData, lossReasonItemsParams, outcomeTrackingEnabled]);

  const handleLossReasonItemFilterChange = (event) => {
    setLossReasonItemFilter(event.target.value);
  };

  const handleOpenLossItem = useCallback((item) => {
    if (!item?.openUrlTarget) return;
    history.push(item.openUrlTarget);
  }, [history]);

  const averageSaleMetric = {
    label: "Ticket médio",
    value: formatMetricValue(averageSaleValue(counters.pipelineWonValue, counters.pipelineWonCount), "currency"),
    change: null,
    icon: <MonetizationOnOutlinedIcon />,
    isText: true,
  };

  const metrics = [
    {
      label: i18n.t("dashboard.counters.inTalk"),
      value: counters.supportHappening || 0,
      change: calculateChange(counters.supportHappening, counters.previousSupportHappening),
      icon: <ChatRoundedIcon />,
    },
    {
      label: i18n.t("dashboard.counters.waiting"),
      value: counters.supportPending || 0,
      change: calculateChange(counters.supportPending, counters.previousSupportPending),
      icon: <HourglassEmptyRoundedIcon />,
    },
    {
      label: i18n.t("dashboard.counters.finished"),
      value: counters.supportFinished || 0,
      change: calculateChange(counters.supportFinished, counters.previousSupportFinished),
      icon: <CheckCircleRoundedIcon />,
    },
    {
      label: i18n.t("dashboard.counters.newContacts"),
      className: outcomeTrackingEnabled ? classes.hideOnThreeColumns : undefined,
      value: contactsCount || 0,
      change: calculateChange(contactsCount, counters.previousNewContacts),
      icon: <PeopleAltRoundedIcon />,
    },
    {
      label: i18n.t("dashboard.counters.averageTalkTime"),
      value: formatTime(counters.avgSupportTime || 0),
      change: calculateChange(counters.avgSupportTime, counters.previousAvgSupportTime),
      icon: <AccessTimeRoundedIcon />,
      isTime: true,
    },
    {
      label: i18n.t("dashboard.counters.averageWaitTime"),
      value: formatTime(counters.avgWaitTime || 0),
      change: calculateChange(counters.avgWaitTime, counters.previousAvgWaitTime),
      icon: <TimerRoundedIcon />,
      isTime: true,
    },
    ...(outcomeTrackingEnabled ? [{ ...averageSaleMetric, className: classes.threeColumnsOnly }] : []),
    ...(outcomeTrackingEnabled ? [
      {
        label: i18n.t("dashboard.pipelineWon"),
        value: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(counters.pipelineWonValue || 0),
        change: null,
        icon: <TrendingUpIcon />,
        isText: true,
      },
      {
        label: i18n.t("dashboard.pipelineLost"),
        value: new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(counters.pipelineLostValue || 0),
        change: null,
        icon: <TrendingDownIcon />,
        isText: true,
      },
      {
        label: i18n.t("dashboard.conversionRate"),
        value: formatMetricValue(metricPercentage(counters.pipelineConversionRate), 'percent'),
        change: null,
        icon: <CheckCircleRoundedIcon />,
        isText: true,
      },
    ] : []),
    ...(outcomeTrackingEnabled ? [{ ...averageSaleMetric, className: classes.twoColumnsOnly }] : []),
  ];

  // Custom tooltip compacto
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          style={{
            backgroundColor: theme.palette.background.paper,
            padding: '8px',
            borderRadius: '6px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Typography variant="caption" style={{ fontWeight: 600, marginBottom: 2 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="caption"
              style={{ color: entry.color, display: 'block', fontSize: 11 }}
            >
              {entry.name}: {formatIndicatorValue(entry.value, entry.payload?.[`${entry.dataKey}Type`] || "integer")}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  function renderFilters() {
    if (filterType === 1) {
      return (
        <>
          <Box className={classes.filterField}>
            <DateTimeField
              label={i18n.t("dashboard.filters.initialDate")}
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: dateTo
              }}
            />
          </Box>
          <Box className={classes.filterField}>
            <DateTimeField
              label={i18n.t("dashboard.filters.finalDate")}
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              fullWidth
              variant="outlined"
              size="small"
              className={classes.dateField}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: dateFrom
              }}
            />
          </Box>
        </>
      );
    } else {
      return (
        <Box className={classes.filterField}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>{i18n.t("dashboard.periodSelect.title")}</InputLabel>
            <Select
              value={period}
              onChange={(e) => handleChangePeriod(e.target.value)}
              label={i18n.t("dashboard.periodSelect.title")}
            >
              <MenuItem value={0}>{i18n.t("dashboard.periodSelect.options.none")}</MenuItem>
              <MenuItem value={3}>{i18n.t("dashboard.periodSelect.options.last3")}</MenuItem>
              <MenuItem value={7}>{i18n.t("dashboard.periodSelect.options.last7")}</MenuItem>
              <MenuItem value={15}>{i18n.t("dashboard.periodSelect.options.last15")}</MenuItem>
              <MenuItem value={30}>{i18n.t("dashboard.periodSelect.options.last30")}</MenuItem>
              <MenuItem value={60}>{i18n.t("dashboard.periodSelect.options.last60")}</MenuItem>
              <MenuItem value={90}>{i18n.t("dashboard.periodSelect.options.last90")}</MenuItem>
            </Select>
          </FormControl>
        </Box>
      );
    }
  }

  function renderKanbanBoardFilter() {
    const kanbanLabel = i18n.t("dashboard.filters.kanbanBoard") || "Quadro Kanban";
    return (
      <Box className={classes.filterField}>
        <FormControl variant="outlined" fullWidth size="small">
          <InputLabel shrink htmlFor="dashboard-kanban-board-filter">{kanbanLabel}</InputLabel>
          <Select
            value={selectedKanbanBoardId}
            onChange={(e) => setSelectedKanbanBoardId(e.target.value)}
            input={<OutlinedInput notched label={kanbanLabel} id="dashboard-kanban-board-filter" />}
            displayEmpty
            renderValue={getKanbanBoardFilterLabel}
          >
            <MenuItem value="">{i18n.t("dashboard.filters.allKanbanBoards") || "Todos os quadros"}</MenuItem>
            {kanbanBoards.map(board => (
              <MenuItem key={board.id} value={board.id}>
                {board.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
  }

  function renderPerformanceIndicatorSelect(value, onChange, label) {
    const outcomeIndicators = new Set(["wonCount", "lostCount", "wonValue", "lostValue", "conversionRate", "avgConversionTime"]);
    const availableIndicators = PERFORMANCE_INDICATORS.filter(
      indicator => outcomeTrackingEnabled || !outcomeIndicators.has(indicator.value)
    );
    return (
      <FormControl variant="outlined" size="small" className={classes.chartSelect}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={availableIndicators.some(i => i.value === value) ? value : "none"}
          onChange={(event) => onChange(event.target.value)}
          label={label}
        >
          {availableIndicators.map(indicator => (
            <MenuItem key={indicator.value} value={indicator.value}>
              {translateIndicator(indicator)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  }

  const filterToggle = (
    <Button
      onClick={() => setShowFilters(!showFilters)}
      className={classes.filterButton}
      startIcon={<FilterListRoundedIcon style={{ fontSize: 16 }} />}
      endIcon={showFilters ? <ExpandLessIcon style={{ fontSize: 16 }} /> : <ExpandMoreIcon style={{ fontSize: 16 }} />}
      size="small"
    >
      {i18n.t("dashboard.filters.title") || "Filtros"}
    </Button>
  );

  return (
    <Box className={classes.root}>
      <Container maxWidth={false} className={classes.container}>
        {/* Header Compacto */}
        <Box className={classes.pageHeader}>
          <Typography className={classes.pageTitle}>
            {i18n.t("dashboard.title") || "Dashboard"}
          </Typography>
          {isPhone && filterToggle}
          <Box display="flex" alignItems="center" className={classes.headerChips} style={{ gap: 8 }}>
            <PageRefreshIndicator visible={isFetching && !!dashboardData} />
            <Box className={classes.boardChip}>
              <DashboardOutlinedIcon style={{ fontSize: 16 }} />
              <Typography variant="caption" component="span">
                {selectedKanbanBoard?.name ||
                  i18n.t("dashboard.filters.allKanbanBoards") ||
                  "Todos os quadros"}
              </Typography>
            </Box>
            <Box className={classes.dateRange}>
              <CalendarTodayIcon style={{ fontSize: 16 }} />
              <Typography variant="caption">
                {dateFrom && dateTo ?
                  `${moment(dateFrom).format("DD/MM")} - ${moment(dateTo).format("DD/MM/YY")}` :
                  moment().format("DD MMM YYYY")
                }
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Filter Section - Agora acima dos cards */}
        <Box className={classes.filterSection}>
          {!isPhone && filterToggle}

          <Collapse in={showFilters}>
            <Paper className={classes.filterContent} elevation={0}>
              <Box className={classes.filterGrid}>
                <Box className={classes.filterField}>
                  <FormControl variant="outlined" fullWidth size="small">
                    <InputLabel>{i18n.t("dashboard.filters.filterType.title")}</InputLabel>
                    <Select
                      value={filterType}
                      onChange={(e) => handleChangeFilterType(e.target.value)}
                      label={i18n.t("dashboard.filters.filterType.title")}
                    >
                      <MenuItem value={1}>{i18n.t("dashboard.filters.filterType.options.perDate")}</MenuItem>
                      <MenuItem value={2}>{i18n.t("dashboard.filters.filterType.options.perPeriod")}</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {renderFilters()}
                {renderKanbanBoardFilter()}

                <Box className={classes.filterField}>
                  <ButtonWithSpinner
                    loading={isFetching}
                    onClick={() => fetchData()}
                    variant="contained"
                    fullWidth
                    className={classes.applyButton}
                    startIcon={<SearchRoundedIcon style={{ fontSize: 16 }} />}
                    size="small"
                  >
                    {i18n.t("dashboard.buttons.filter")}
                  </ButtonWithSpinner>
                </Box>
              </Box>
            </Paper>
          </Collapse>
        </Box>

        {/* Metrics Cards - 6 cards compactos (3x3) */}
        <Grid container spacing={isSmallScreen ? 2 : 3} className={classes.metricsRow}>
          {metrics.map((metric, index) => (
            <Grid item xs={12} sm={6} md={4} key={index} className={metric.className}>
              <Paper className={classes.metricCard} elevation={0}>
                <Box className={classes.metricContent}>
                  <Box className={classes.metricInfo}>
                    <Box className={classes.metricTitleRow}>
                      <Typography className={classes.metricLabel}>
                        {metric.label}
                      </Typography>
                      {metric.change !== null && metric.change !== undefined && (
                        <Box
                          className={`${classes.metricChange} ${
                            metric.change > 0
                              ? classes.positiveChange
                              : metric.change < 0
                                ? classes.negativeChange
                                : classes.neutralChange
                          }`}
                        >
                          {metric.change > 0 ? (
                            <TrendingUpIcon style={{ fontSize: 11 }} />
                          ) : metric.change < 0 ? (
                            <TrendingDownIcon style={{ fontSize: 11 }} />
                          ) : null}
                          <span>{Math.abs(metric.change)}%</span>
                        </Box>
                      )}
                    </Box>
                    <Typography className={classes.metricValue}>
                      {metric.value}
                    </Typography>
                  </Box>
                  <Box className={classes.metricIconBox}>
                    {metric.icon}
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {isLoading && !dashboardData ? (
          <PageContentLoader variant="dashboard" />
        ) : (
          <>
            {/* Charts Section Compacto */}
            <Grid container spacing={2} className={classes.dashboardGridRow}>
              {/* Performance dos Atendentes */}
              {attendants.length > 0 && (
                <Grid item xs={12} lg={8}>
                  <Paper className={classes.chartCard} elevation={0}>
                    <Box className={classes.chartHeader}>
                      <Box>
                        <Typography className={classes.chartTitle}>
                          {i18n.t("dashboard.charts.performanceTitle") || "Performance dos Atendentes"}
                        </Typography>
                        <Typography className={classes.chartSubtitle}>
                          {i18n.t("dashboard.charts.performanceSubtitle") || "Atendimentos realizados no período"}
                        </Typography>
                      </Box>
                      <Box className={classes.chartHeaderControls}>
                        {renderPerformanceIndicatorSelect(
                          performanceIndicator1,
                          setPerformanceIndicator1,
                          i18n.t("dashboard.performanceIndicators.axisLeft") || "Eixo esquerdo"
                        )}
                        {renderPerformanceIndicatorSelect(
                          performanceIndicator2,
                          setPerformanceIndicator2,
                          i18n.t("dashboard.performanceIndicators.axisRight") || "Eixo direito"
                        )}
                      </Box>
                    </Box>
                    {performanceChartData.length > 0 && (hasPerformanceIndicator1 || hasPerformanceIndicator2) ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <ComposedChart data={performanceChartData} margin={{ top: 20, right: 8, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} vertical={false} />
                          <XAxis
                            dataKey="name"
                            stroke={theme.palette.text.secondary}
                            tick={{ fontSize: 11 }}
                            axisLine={false}
                          />
                          {hasPerformanceIndicator1 && (
                            <YAxis
                              yAxisId="left"
                              stroke={theme.palette.text.secondary}
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => formatIndicatorValue(value, performanceIndicatorConfig1.type)}
                              axisLine={false}
                              domain={computeAxisDomain(
                                performanceChartData.map((d) => d[performanceIndicatorConfig1.value]),
                                performanceIndicatorConfig1.type
                              )}
                              allowDecimals={performanceIndicatorConfig1.type !== "integer"}
                            />
                          )}
                          {hasPerformanceIndicator2 && (
                            <YAxis
                              yAxisId="right"
                              orientation="right"
                              stroke={theme.palette.text.secondary}
                              tick={{ fontSize: 11 }}
                              tickFormatter={(value) => formatIndicatorValue(value, performanceIndicatorConfig2.type)}
                              axisLine={false}
                              domain={computeAxisDomain(
                                performanceChartData.map((d) => d[performanceIndicatorConfig2.value]),
                                performanceIndicatorConfig2.type
                              )}
                              allowDecimals={performanceIndicatorConfig2.type !== "integer"}
                            />
                          )}
                          {!hasPerformanceIndicator1 && !hasPerformanceIndicator2 && (
                            <YAxis
                            stroke={theme.palette.text.secondary}
                            tick={{ fontSize: 11 }}
                            axisLine={false}
                            />
                          )}
                          <Tooltip content={<CustomTooltip />} />
                          {hasPerformanceIndicator2 && (
                            <Bar
                              yAxisId="right"
                              dataKey={performanceIndicatorConfig2.value}
                              name={translateIndicator(performanceIndicatorConfig2)}
                              fill={isLossIndicator(performanceIndicatorConfig2.value) ? "#f44336" : theme.palette.primary.main}
                              radius={[6, 6, 0, 0]}
                              barSize={30}
                              className={classes.barElement}
                            />
                          )}
                          {hasPerformanceIndicator1 && (() => {
                            const lineColor = isLossIndicator(performanceIndicatorConfig1.value) ? "#d32f2f" : "#0F7A3E";
                            return (
                              <Line
                                yAxisId="left"
                                type="monotone"
                                dataKey={performanceIndicatorConfig1.value}
                                name={translateIndicator(performanceIndicatorConfig1)}
                                stroke={lineColor}
                                strokeWidth={3}
                                dot={{ r: 4, fill: lineColor, stroke: lineColor }}
                                activeDot={{ r: 5, fill: lineColor, stroke: lineColor }}
                              />
                            );
                          })()}
                        </ComposedChart>
                      </ResponsiveContainer>
                    ) : (
                      <Box textAlign="center" py={6}>
                        <Typography color="textSecondary" variant="caption">
                          {i18n.t("dashboard.noData.performance") || "Nenhum dado de performance disponível"}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}

              {/* Distribuição de Status Compacto */}
              <Grid item xs={12} lg={attendants.length > 0 ? 4 : 12}>
                <Paper className={classes.pieChartCard} elevation={0}>
                  <Box className={classes.chartHeader}>
                    <Box>
                      <Typography className={classes.chartTitle}>
                        Distribuição de Status
                      </Typography>
                      <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>
                        {totalTickets}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        Total de Atendimentos
                      </Typography>
                    </Box>
                  </Box>

                  {totalTickets > 0 ? (
                    <>
                      <ResponsiveContainer width="100%" height={140}>
                        <PieChart>
                          <Pie
                            data={pieChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={60}
                            fill="#8884d8"
                            dataKey="value"
                            className={classes.pieElement}
                          >
                            {pieChartData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                        </PieChart>
                      </ResponsiveContainer>
                      <Box mt={1}>
                        {pieChartData.map((item, index) => (
                          <Box key={index} display="flex" justifyContent="space-between" mb={0.5}>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Box width={8} height={8} borderRadius={1} bgcolor={item.color} />
                              <Typography variant="caption" style={{ fontSize: 11 }}>{item.name}</Typography>
                            </Box>
                            <Box display="flex" alignItems="center" gap={0.5}>
                              <Typography variant="caption" style={{ fontWeight: 600, fontSize: 11 }}>
                                {item.value}
                              </Typography>
                              <Typography variant="caption" color="textSecondary" style={{ fontSize: 10 }}>
                                ({item.percentage}%)
                              </Typography>
                            </Box>
                          </Box>
                        ))}
                      </Box>

                      {/* Estatísticas adicionais compactas */}
                      <Box mt={2}>
                        <Box className={classes.statItem}>
                          <Typography className={classes.statLabel}>
                            {i18n.t("dashboard.averageTalkTime") || "Tempo Médio"}
                          </Typography>
                          <Typography className={classes.statValue}>
                            {formatTime(counters.avgSupportTime || 0)}
                          </Typography>
                        </Box>
                        <Box className={classes.statItem}>
                          <Typography className={classes.statLabel}>
                            {i18n.t("dashboard.averageWaitTime") || "Espera Média"}
                          </Typography>
                          <Typography className={classes.statValue}>
                            {formatTime(counters.avgWaitTime || 0)}
                          </Typography>
                        </Box>
                      </Box>
                    </>
                  ) : (
                    <Box textAlign="center" py={3}>
                      <Typography color="textSecondary" variant="caption">
                        {i18n.t("dashboard.noData.tickets") || "Nenhum atendimento no período"}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Grid>
            </Grid>

            {/* Faixa de perdas */}
            {outcomeTrackingEnabled && (
              <Grid container spacing={2} className={`${classes.dashboardGridRow} ${classes.finalDashboardRow}`}>
                {!lossReasonItemsExpanded && (
                  <Grid item xs={12} lg={4}>
                    <Paper className={`${classes.pieChartCard} ${classes.fixedDashboardCard}`} elevation={0}>
                      <Box className={classes.chartHeader}>
                        <Box>
                          <Typography className={classes.chartTitle}>
                            Análise de Perdas
                          </Typography>
                          <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>
                            {lossReasonTotal}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Total de perdas
                          </Typography>
                        </Box>
                        <FormControl variant="outlined" size="small" style={{ minWidth: 132 }}>
                          <InputLabel shrink htmlFor="dashboard-loss-reason-user-filter">Usuário</InputLabel>
                          <Select
                            value={lossReasonUserId}
                            onChange={handleLossReasonUserChange}
                            input={<OutlinedInput notched label="Usuário" id="dashboard-loss-reason-user-filter" />}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) return "Todos";
                              const attendant = attendants.find(item => String(item.id) === String(selected));
                              return attendant?.name || "Todos";
                            }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {attendants.map((attendant) => (
                              <MenuItem key={attendant.id} value={attendant.id}>
                                {attendant.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>

                      {lossReasonTotal > 0 ? (
                        <Box className={classes.lossReasonBody}>
                          <Box
                            className={classes.lossReasonChartArea}
                            style={{ height: lossReasonChartHeight }}
                          >
                            <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={lossReasonDisplayData}
                                  cx="50%"
                                  cy="50%"
                                  innerRadius={lossReasonInnerRadius}
                                  outerRadius={lossReasonOuterRadius}
                                  fill={theme.palette.primary.main}
                                  dataKey="value"
                                  className={classes.pieElement}
                                >
                                  {lossReasonDisplayData.map((entry, index) => (
                                    <Cell key={`loss-cell-${index}`} fill={entry.color} />
                                  ))}
                                </Pie>
                                <Tooltip content={<CustomTooltip />} />
                              </PieChart>
                            </ResponsiveContainer>
                          </Box>
                          <Box className={classes.lossReasonLegend}>
                            {lossReasonDisplayData.map((item, index) => (
                              <Box
                                key={item.id || index}
                                className={`${classes.lossReasonRow} ${item.isOthers ? classes.lossReasonOthers : ""}`.trim()}
                                onMouseEnter={item.isOthers ? event => setLossReasonOthersAnchorEl(event.currentTarget) : undefined}
                                onMouseLeave={item.isOthers ? () => setLossReasonOthersAnchorEl(null) : undefined}
                                onClick={item.isOthers ? event => setLossReasonOthersAnchorEl(event.currentTarget) : undefined}
                              >
                                <Box className={classes.lossReasonName}>
                                  <Box width={8} height={8} borderRadius={1} bgcolor={item.color} flexShrink={0} />
                                  <Typography variant="caption" className={classes.lossReasonLabel}>
                                    {item.name}
                                  </Typography>
                                </Box>
                                <Box className={classes.lossReasonValue}>
                                  <Typography variant="caption" style={{ fontWeight: 600, fontSize: 12 }}>
                                    {item.value}
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                                    ({item.percentage}%)
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </Box>
                          <Popover
                            open={Boolean(lossReasonOthersAnchorEl) && lossReasonOthers.length > 0}
                            anchorEl={lossReasonOthersAnchorEl}
                            onClose={() => setLossReasonOthersAnchorEl(null)}
                            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                            transformOrigin={{ vertical: "top", horizontal: "left" }}
                            disableRestoreFocus
                            style={{ pointerEvents: "none" }}
                          >
                            <Box className={classes.lossReasonPopover}>
                              <Typography variant="subtitle2" style={{ fontWeight: 700, marginBottom: 8 }}>
                                Outros motivos
                              </Typography>
                              {lossReasonOthers.map((item, index) => (
                                <Box key={item.id || index} className={classes.lossReasonRow}>
                                  <Typography variant="caption" className={classes.lossReasonLabel}>
                                    {item.name}
                                  </Typography>
                                  <Box className={classes.lossReasonValue}>
                                    <Typography variant="caption" style={{ fontWeight: 600, fontSize: 12 }}>
                                      {item.value}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" style={{ fontSize: 11 }}>
                                      ({item.percentage}%)
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </Popover>
                        </Box>
                      ) : (
                        <Box flex={1} display="flex" alignItems="center" justifyContent="center" textAlign="center">
                          <Typography color="textSecondary" variant="caption">
                            Nenhuma perda no período
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>
                )}

                <Grid item xs={12} lg={lossReasonItemsExpanded ? 12 : 8}>
                  <Paper
                    className={`${classes.tableCard} ${lossReasonItemsExpanded ? classes.expandedDashboardCard : classes.fixedDashboardCard}`}
                    elevation={0}
                  >
                    <Box className={classes.lossItemsCardHeader}>
                      <Box>
                        <Typography className={classes.chartTitle}>
                          Tickets Perdidos
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {lossReasonItemsLoading
                            ? "Carregando perdas recentes"
                            : `${lossReasonItems.length}${lossReasonItemsCount > lossReasonItems.length ? ` de ${lossReasonItemsCount}` : ""} perdas encontradas`}
                        </Typography>
                      </Box>
                      <Box className={classes.lossItemsHeaderActions}>
                        <FormControl variant="outlined" size="small" className={classes.lossItemsFilter}>
                          <InputLabel shrink htmlFor="dashboard-loss-item-reason-filter">Motivo</InputLabel>
                          <Select
                            value={lossReasonItemFilter}
                            onChange={handleLossReasonItemFilterChange}
                            input={<OutlinedInput notched label="Motivo" id="dashboard-loss-item-reason-filter" />}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) return "Todos";
                              return selectedLossReasonItemOption?.label || (selected === "none" ? "Sem motivo" : "Motivo selecionado");
                            }}
                          >
                            <MenuItem value="">Todos</MenuItem>
                            {lossReasonItemFilter && !selectedLossReasonItemOption && (
                              <MenuItem value={lossReasonItemFilter} style={{ display: "none" }}>
                                {lossReasonItemFilter === "none" ? "Sem motivo" : "Motivo selecionado"}
                              </MenuItem>
                            )}
                            {lossReasonItemOptions.map((option) => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton
                          size="small"
                          className={classes.expandButton}
                          onClick={() => setLossReasonItemsExpanded(previous => !previous)}
                          aria-label={lossReasonItemsExpanded ? "Recolher tickets perdidos" : "Expandir tickets perdidos"}
                        >
                          {lossReasonItemsExpanded ? <FullscreenExitIcon /> : <FullscreenIcon />}
                        </IconButton>
                      </Box>
                    </Box>

                    {lossReasonItemsLoading ? (
                      <Box className={classes.lossItemsEmpty}>
                        <Typography color="textSecondary" variant="caption">
                          Carregando tickets perdidos...
                        </Typography>
                      </Box>
                    ) : lossReasonItems.length > 0 ? (
                      <Box className={classes.lossItemsTableWrapper}>
                        <table className={classes.compactTable} style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                TICKET/CARD
                              </th>
                              {lossReasonItemsExpanded && (
                                <th style={{ textAlign: "center", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  TIPO
                                </th>
                              )}
                              <th style={{ textAlign: "left", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                MOTIVO
                              </th>
                              {lossReasonItemsExpanded && (
                                <th style={{ textAlign: "left", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  RESPONSÁVEL
                                </th>
                              )}
                              <th style={{ textAlign: "center", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                DATA
                              </th>
                              <th style={{ textAlign: "right", color: theme.palette.text.secondary, fontWeight: 600, borderBottom: `1px solid ${theme.palette.divider}` }}>
                                VALOR
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {lossReasonItems.map((item) => (
                              <tr
                                key={`${item.type}-${item.id}`}
                                className={classes.lossItemsRow}
                                onClick={() => handleOpenLossItem(item)}
                              >
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  <Typography className={classes.lossItemsTitle}>
                                    {item.title || item.contactName || `${item.type === "card" ? "Card" : "Ticket"} #${item.id}`}
                                  </Typography>
                                  {item.contactName && item.contactName !== item.title && (
                                    <Typography className={classes.lossItemsMeta}>
                                      {item.contactName}
                                    </Typography>
                                  )}
                                </td>
                                {lossReasonItemsExpanded && (
                                  <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
                                    <Chip
                                      label={item.type === "card" ? "Card" : "Ticket"}
                                      size="small"
                                      style={{
                                        height: 20,
                                        backgroundColor: `${theme.palette.primary.main}15`,
                                        color: theme.palette.primary.main,
                                        fontWeight: 600,
                                        fontSize: 10,
                                      }}
                                    />
                                  </td>
                                )}
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                                    {item.lossReasonId
                                      ? lossReasonLabelByValue.get(String(item.lossReasonId)) || item.lossReasonName || "Motivo"
                                      : "Sem motivo"}
                                  </Typography>
                                </td>
                                {lossReasonItemsExpanded && (
                                  <td style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                                    <Typography style={{ fontSize: 12 }}>
                                      {item.outcomeUserName || "Sem responsável"}
                                    </Typography>
                                  </td>
                                )}
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: "center" }}>
                                  <Typography style={{ fontSize: 12 }}>
                                    {formatLossDate(item.outcomeResolvedAt)}
                                  </Typography>
                                </td>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: "right" }}>
                                  <Typography style={{ fontSize: 12, fontWeight: 700, color: theme.palette.primary.main }}>
                                    {formatCurrency(item.value)}
                                  </Typography>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                    ) : (
                      <Box className={classes.lossItemsEmpty}>
                        <Typography color="textSecondary" variant="caption">
                          Nenhuma perda encontrada para os filtros atuais
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            )}

            {/* Status dos atendentes */}
            {attendants.length > 0 && (
              <Grid container spacing={2} className={`${classes.dashboardGridRow} ${classes.finalDashboardRow}`}>
                <Grid item xs={12}>
                  <Paper className={classes.tableCard} elevation={0}>
                    <Box className={classes.attendantsCardHeader}>
                      <Box>
                        <Typography className={classes.chartTitle}>
                          {i18n.t("dashboard.attendants.title") || "Status dos Atendentes"}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {attendants.length} {i18n.t("dashboard.attendants.active") || "atendentes ativos"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box className={classes.attendantsTableWrapper}>
                      <table className={classes.compactTable} style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{
                              textAlign: 'left',
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                              ATENDENTE
                            </th>
                            <th style={{
                              textAlign: 'center',
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                              STATUS
                            </th>
                            <th style={{
                              textAlign: 'center',
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                              ATENDIMENTOS
                            </th>
                            <th style={{
                              textAlign: 'center',
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                              TEMPO MÉDIO
                            </th>
                            <th style={{
                              textAlign: 'center',
                              color: theme.palette.text.secondary,
                              fontWeight: 600,
                              borderBottom: `1px solid ${theme.palette.divider}`,
                            }}>
                              CONCLUSÃO
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {attendants.map((attendant, index) => {
                            const totalTickets = (attendant.ticketsCount || attendant.tickets || 0);
                            const closedTickets = (attendant.closedTickets || Math.floor(totalTickets * 0.75));
                            const completionRate = totalTickets > 0 ? ((closedTickets / totalTickets) * 100).toFixed(0) : 0;
                            const isOnline = attendant.online || attendant.status === 'online';

                            return (
                              <tr key={index}>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
                                  <Typography style={{ fontWeight: 600, fontSize: 12 }}>
                                    {attendant.name}
                                  </Typography>
                                </td>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                  <Chip
                                    label={isOnline ? 'Online' : 'Offline'}
                                    size="small"
                                    style={{
                                      height: 20,
                                      backgroundColor: isOnline ? `${theme.palette.success.main}15` : `${theme.palette.error.main}15`,
                                      color: isOnline ? theme.palette.success.main : theme.palette.error.main,
                                      fontWeight: 600,
                                      fontSize: 10,
                                    }}
                                  />
                                </td>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                  <Typography style={{ fontSize: 14, fontWeight: 700, color: theme.palette.primary.main }}>
                                    {totalTickets}
                                  </Typography>
                                </td>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                  <Typography style={{ fontSize: 12, fontWeight: 600 }}>
                                    {formatTime(attendant.avgSupportTime || 0)}
                                  </Typography>
                                </td>
                                <td style={{ borderBottom: `1px solid ${theme.palette.divider}`, textAlign: 'center' }}>
                                  <Box display="flex" alignItems="center" justifyContent="center" gap={0.5}>
                                    <Box style={{ flex: 1, maxWidth: 60 }}>
                                      <Box
                                        style={{
                                          height: 4,
                                          backgroundColor: `${theme.palette.primary.main}20`,
                                          borderRadius: 2,
                                          overflow: 'hidden',
                                        }}
                                      >
                                        <Box
                                          style={{
                                            height: '100%',
                                            width: `${completionRate}%`,
                                            backgroundColor: theme.palette.primary.main,
                                          }}
                                        />
                                      </Box>
                                    </Box>
                                    <Typography style={{
                                      fontSize: 11,
                                      fontWeight: 600,
                                      color: theme.palette.primary.main,
                                      minWidth: 30,
                                    }}>
                                      {completionRate}%
                                    </Typography>
                                  </Box>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </Box>

                    <Box
                      mt={2}
                      pt={1.5}
                      borderTop={`1px solid ${theme.palette.divider}`}
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Box display="flex" alignItems="center" style={{ gap: 16 }}>
                        <Box>
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: 'block', marginBottom: 4, whiteSpace: 'nowrap', letterSpacing: 0.4 }}>
                            TOTAL
                          </Typography>
                          <Typography style={{ fontSize: 16, fontWeight: 700, color: theme.palette.primary.main }}>
                            {attendants.reduce((sum, att) => sum + (att.ticketsCount || att.tickets || 0), 0)}
                          </Typography>
                        </Box>
                        <Box style={{ width: 1, alignSelf: 'stretch', backgroundColor: theme.palette.divider }} />
                        <Box>
                          <Typography variant="caption" color="textSecondary" style={{ fontSize: 10, display: 'block', marginBottom: 4, whiteSpace: 'nowrap', letterSpacing: 0.4 }}>
                            TEMPO MÉDIO
                          </Typography>
                          <Typography style={{ fontSize: 16, fontWeight: 700, color: theme.palette.primary.main }}>
                            {formatTime(counters.avgSupportTime || 0)}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Typography variant="caption" style={{ fontSize: 10, color: theme.palette.text.secondary }}>
                          <span style={{ color: theme.palette.primary.main, fontWeight: 600 }}>
                            {attendants.filter(a => a.online || a.status === 'online').length}
                          </span> online •
                          <span style={{ marginLeft: 4 }}>
                            {attendants.filter(a => !a.online && a.status !== 'online').length}
                          </span> offline
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            )}

          </>
        )}
      </Container>
    </Box>
  );
};

export default Dashboard;
